import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { leadService } from '../../../services/leadService';

const LeadManagementPanel = () => {
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    status: 'cold',
    value: '',
    source: '',
    assignedTo: '',
    notes: ''
  });

  const convertToDisplayFormat = (lead) => ({
    id: lead?.id,
    name: lead?.name,
    email: lead?.email,
    phone: lead?.phone,
    service: lead?.service,
    status: lead?.status,
    value: lead?.value,
    source: lead?.source,
    assignedTo: lead?.assignedTo,
    avatar: lead?.avatar,
    avatarAlt: lead?.avatarAlt,
    lastContact: lead?.lastContact ? new Date(lead.lastContact)?.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }) : 'N/A'
  });

  useEffect(() => {
    loadLeads();
    
    // Subscribe to real-time changes
    const unsubscribe = leadService?.subscribeToChanges((payload) => {
      // payload.new from Supabase is in snake_case, convert before display
      const convertPayload = (raw) => ({
        id: raw?.id,
        name: raw?.name,
        email: raw?.email,
        phone: raw?.phone,
        service: raw?.service,
        status: raw?.status,
        value: raw?.value,
        source: raw?.source,
        assignedTo: raw?.assigned_to,
        avatar: raw?.avatar,
        avatarAlt: raw?.avatar_alt,
        lastContact: raw?.last_contact,
      });
      if (payload?.eventType === 'INSERT') {
        setLeads(prev => [convertToDisplayFormat(convertPayload(payload?.new)), ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setLeads(prev => prev?.map(lead =>
          lead?.id === payload?.new?.id ? convertToDisplayFormat(convertPayload(payload?.new)) : lead
        ));
      } else if (payload?.eventType === 'DELETE') {
        setLeads(prev => prev?.filter(lead => lead?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const data = await leadService?.getAll();
      setLeads(data?.map(convertToDisplayFormat));
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'Tous les prospects' },
    { value: 'hot', label: 'Hot' },
    { value: 'warm', label: 'Warm' },
    { value: 'cold', label: 'Cold' }
  ];

  const filteredLeads = selectedStatus === 'all'
    ? leads
    : leads?.filter(lead => lead?.status === selectedStatus);

  const getStatusColor = (status) => {
    const colors = {
      hot: 'var(--color-error)',
      warm: 'var(--color-warning)',
      cold: 'var(--color-trust)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce prospect?')) return;

    try {
      await leadService?.delete(leadId);
      setLeads(prev => prev?.filter(lead => lead?.id !== leadId));
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleOpenModal = (lead = null) => {
    if (lead) {
      setEditingLead(lead);
      setFormData({
        name: lead?.name || '',
        email: lead?.email || '',
        phone: lead?.phone || '',
        service: lead?.service || '',
        status: lead?.status || 'cold',
        value: lead?.value || '',
        source: lead?.source || '',
        assignedTo: lead?.assignedTo || '',
        notes: lead?.notes || ''
      });
    } else {
      setEditingLead(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        status: 'cold',
        value: '',
        source: '',
        assignedTo: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLead(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      status: 'cold',
      value: '',
      source: '',
      assignedTo: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      if (editingLead) {
        await leadService?.update(editingLead?.id, formData);
      } else {
        await leadService?.create(formData);
      }
      handleCloseModal();
      loadLeads();
    } catch (err) {
      setError(err?.message);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-muted-foreground">Loading leads...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-error">Error: {error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-xl shadow-premium border border-border">
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">Lead Management</h3>
              <p className="text-sm text-muted-foreground">Track and manage client inquiries</p>
            </div>
            <div className="flex items-center gap-3">
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                className="w-40" />

              <Button 
                variant="default" 
                iconName="UserPlus" 
                iconPosition="left" 
                size="sm"
                onClick={() => handleOpenModal()}
              >
                Add Lead
              </Button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6">
          <div className="space-y-4">
            {filteredLeads?.map((lead) => (
              <div
                key={lead?.id}
                className="bg-muted/50 rounded-xl p-4 hover:bg-muted transition-colors">

                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Image
                      src={lead?.avatar}
                      alt={lead?.avatarAlt}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0" />

                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                        {lead?.name}
                      </h4>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <Icon name="Mail" size={12} />
                          {lead?.email}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Icon name="Phone" size={12} />
                          {lead?.phone}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2 py-1 bg-background rounded text-xs font-medium text-foreground">
                          {lead?.service}
                        </span>
                        <span
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${getStatusColor(lead?.status)}15`,
                            color: getStatusColor(lead?.status)
                          }}>

                          {lead?.status?.toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Source: {lead?.source}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row items-start md:items-center gap-3 lg:gap-4">
                    <div className="text-left md:text-right">
                      <p className="text-lg font-bold text-foreground">{lead?.value}</p>
                      <p className="text-xs text-muted-foreground">Est. Value</p>
                    </div>
                    <div className="text-left md:text-right">
                      <p className="text-sm font-medium text-foreground">{lead?.assignedTo || 'Unassigned'}</p>
                      <p className="text-xs text-muted-foreground">Assigned To</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleOpenModal(lead)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors"
                      >
                        <Icon name="Edit" size={16} className="text-primary" />
                      </button>
                      <button className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors">
                        <Icon name="Phone" size={16} className="text-primary" />
                      </button>
                      <button className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors">
                        <Icon name="Mail" size={16} className="text-primary" />
                      </button>
                      <button 
                        onClick={() => handleDelete(lead?.id)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-error/10 flex items-center justify-center transition-colors"
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingLead ? 'Modifier le Prospect' : 'Nouveau Prospect'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nom complet *"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  required
                />
                <Input
                  label="Email *"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  required
                />
                <Input
                  label="Téléphone *"
                  value={formData?.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                  required
                />
                <Input
                  label="Service *"
                  value={formData?.service}
                  onChange={(e) => setFormData({ ...formData, service: e?.target?.value })}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Statut *</label>
                  <Select
                    options={[
                      { value: 'hot', label: 'Hot' },
                      { value: 'warm', label: 'Warm' },
                      { value: 'cold', label: 'Cold' }
                    ]}
                    value={formData?.status}
                    onChange={(value) => setFormData({ ...formData, status: value })}
                  />
                </div>
                <Input
                  label="Valeur estimée"
                  value={formData?.value}
                  onChange={(e) => setFormData({ ...formData, value: e?.target?.value })}
                />
                <Input
                  label="Source"
                  value={formData?.source}
                  onChange={(e) => setFormData({ ...formData, source: e?.target?.value })}
                />
                <Input
                  label="Assigné à"
                  value={formData?.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e?.target?.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  rows={4}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" variant="default" className="flex-1">
                  {editingLead ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

};

export default LeadManagementPanel;